"use client";

import { useState } from "react";
import Link from "next/link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    ArrowLeft,
    MoreHorizontal,
    UserPlus,
    MessageSquare,
    FileText,
    CheckCircle,
    Clock,
    AlertCircle,
    CheckCheck,
    Archive,
    Phone,
    Mail,
    Calendar,
    MapPin,
    Paperclip,
    Send,
} from "lucide-react";
import {
    Complaint,
    mockCategories,
    mockUsers,
    getStatusConfig,
    getPriorityConfig,
    ComplaintStatus,
} from "@/lib/mock-data";
import { formatDate, formatDateTime } from "@/lib/date-utils";

interface ComplaintDetailClientProps {
    complaint: Complaint | undefined;
    id: string;
}

export function ComplaintDetailClient({ complaint, id }: ComplaintDetailClientProps) {
    const [messageText, setMessageText] = useState("");
    const [selectedStatus, setSelectedStatus] = useState<ComplaintStatus | "">("");

    if (!complaint) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Card className="max-w-md w-full mx-4">
                    <CardContent className="pt-6 text-center">
                        <h2 className="text-xl font-semibold mb-2">ไม่พบเรื่องร้องเรียน</h2>
                        <p className="text-muted-foreground mb-6">ไม่พบข้อมูลเรื่องร้องเรียน ID: {id}</p>
                        <Button asChild>
                            <Link href="/staff/complaints">กลับหน้ารายการ</Link>
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const category = mockCategories.find((c) => c.id === complaint.categoryId);
    const statusConfig = getStatusConfig(complaint.status);
    const priorityConfig = getPriorityConfig(complaint.priority);
    const staffUsers = mockUsers.filter((u) => u.role === "staff");

    const statusIcons: Record<string, React.ReactNode> = {
        submitted: <FileText className="h-5 w-5" />,
        received: <CheckCircle className="h-5 w-5" />,
        in_progress: <Clock className="h-5 w-5" />,
        pending_info: <AlertCircle className="h-5 w-5" />,
        resolved: <CheckCheck className="h-5 w-5" />,
        closed: <Archive className="h-5 w-5" />,
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" asChild>
                        <Link href="/staff/complaints">
                            <ArrowLeft className="h-5 w-5" />
                        </Link>
                    </Button>
                    <div>
                        <h1 className="text-xl font-bold text-primary">{complaint.referenceNumber}</h1>
                        <p className="text-sm text-muted-foreground">{complaint.subject}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Badge
                        variant="secondary"
                        className="text-sm px-3 py-1"
                        style={{ backgroundColor: statusConfig.bgColor, color: statusConfig.darkColor }}
                    >
                        {statusConfig.label}
                    </Badge>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline">
                                <MoreHorizontal className="h-4 w-4 mr-2" />
                                การดำเนินการ
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                                <Clock className="mr-2 h-4 w-4" />
                                เปลี่ยนสถานะ
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                                <UserPlus className="mr-2 h-4 w-4" />
                                มอบหมายผู้รับผิดชอบ
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem>
                                <MessageSquare className="mr-2 h-4 w-4" />
                                ส่งข้อความถึงผู้ร้อง
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Priority & Category */}
                    <div className="flex gap-2">
                        <Badge
                            variant="outline"
                            style={{ borderColor: priorityConfig.color, color: priorityConfig.color }}
                        >
                            ความสำคัญ: {priorityConfig.label}
                        </Badge>
                        <Badge variant="secondary">{category?.name}</Badge>
                    </div>

                    {/* Complaint Details */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">รายละเอียดเรื่องร้องเรียน</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">รายละเอียด</p>
                                <p className="whitespace-pre-wrap">{complaint.description}</p>
                            </div>
                            {complaint.accusedInfo && (
                                <div>
                                    <p className="text-sm text-muted-foreground mb-1">ข้อมูลผู้ถูกร้องเรียน</p>
                                    <p>{complaint.accusedInfo}</p>
                                </div>
                            )}
                            <div className="flex flex-wrap gap-4 text-sm">
                                {complaint.incidentDate && (
                                    <div className="flex items-center gap-2 text-muted-foreground">
                                        <Calendar className="h-4 w-4" />
                                        <span>วันที่เกิดเหตุ: {complaint.incidentDate}</span>
                                    </div>
                                )}
                                {complaint.incidentLocation && (
                                    <div className="flex items-center gap-2 text-muted-foreground">
                                        <MapPin className="h-4 w-4" />
                                        <span>{complaint.incidentLocation}</span>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Attachments */}
                    {complaint.attachments.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">ไฟล์แนบ</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {complaint.attachments.map((file) => (
                                        <div
                                            key={file.id}
                                            className="flex items-center gap-3 p-3 bg-muted rounded-lg"
                                        >
                                            <Paperclip className="h-5 w-5 text-muted-foreground" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium truncate">{file.fileName}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {(file.fileSize / 1024 / 1024).toFixed(2)} MB
                                                </p>
                                            </div>
                                            <Button variant="outline" size="sm">ดู</Button>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Status Timeline */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">ประวัติการดำเนินการ</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="relative pl-8 space-y-6">
                                {complaint.statusLogs.slice().reverse().map((log, index) => {
                                    const logStatusConfig = getStatusConfig(log.status);
                                    return (
                                        <div key={log.id} className="relative">
                                            <div
                                                className="absolute -left-8 h-8 w-8 rounded-full flex items-center justify-center"
                                                style={{ backgroundColor: logStatusConfig.bgColor, color: logStatusConfig.color }}
                                            >
                                                {statusIcons[log.status]}
                                            </div>
                                            {index !== complaint.statusLogs.length - 1 && (
                                                <div className="absolute -left-4 top-8 w-0.5 bg-border h-[calc(100%+1rem)]" />
                                            )}
                                            <div>
                                                <p className="font-medium" style={{ color: logStatusConfig.color }}>
                                                    {logStatusConfig.label}
                                                </p>
                                                <p className="text-sm text-muted-foreground">
                                                    {formatDateTime(log.performedAt)}
                                                    {" • "}
                                                    {log.performedBy}
                                                </p>
                                                {log.comment && (
                                                    <p className="text-sm mt-2 p-3 bg-muted rounded-lg">{log.comment}</p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Add Internal Note */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">บันทึกภายใน</CardTitle>
                            <CardDescription>เพิ่มหมายเหตุสำหรับเจ้าหน้าที่ (ผู้ร้องจะไม่เห็น)</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Textarea
                                placeholder="พิมพ์บันทึกหรือหมายเหตุ..."
                                rows={3}
                                value={messageText}
                                onChange={(e) => setMessageText(e.target.value)}
                            />
                            <Button disabled={!messageText.trim()}>
                                <Send className="mr-2 h-4 w-4" />
                                บันทึก
                            </Button>
                        </CardContent>
                    </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Quick Actions */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">การดำเนินการด่วน</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <Dialog>
                                <DialogTrigger asChild>
                                    <Button variant="outline" className="w-full justify-start">
                                        <Clock className="mr-2 h-4 w-4" />
                                        เปลี่ยนสถานะ
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>เปลี่ยนสถานะเรื่องร้องเรียน</DialogTitle>
                                        <DialogDescription>เลือกสถานะใหม่และระบุหมายเหตุ</DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                        <Select
                                            value={selectedStatus}
                                            onValueChange={(v) => setSelectedStatus(v as ComplaintStatus)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="เลือกสถานะ" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="received">รับเรื่อง</SelectItem>
                                                <SelectItem value="in_progress">ดำเนินการ</SelectItem>
                                                <SelectItem value="pending_info">รอข้อมูลเพิ่ม</SelectItem>
                                                <SelectItem value="resolved">แล้วเสร็จ</SelectItem>
                                                <SelectItem value="closed">ปิดเรื่อง</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Textarea placeholder="หมายเหตุ (ถ้ามี)" />
                                    </div>
                                    <DialogFooter>
                                        <Button>บันทึก</Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>

                            <Button variant="outline" className="w-full justify-start">
                                <UserPlus className="mr-2 h-4 w-4" />
                                มอบหมายผู้รับผิดชอบ
                            </Button>

                            <Button variant="outline" className="w-full justify-start">
                                <MessageSquare className="mr-2 h-4 w-4" />
                                ส่งข้อความถึงผู้ร้อง
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Complainant Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">ข้อมูลผู้ร้อง</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {complaint.complainant.isAnonymous ? (
                                <Badge variant="secondary">ไม่เปิดเผยตัวตน</Badge>
                            ) : (
                                <div className="space-y-3">
                                    <p className="font-medium">{complaint.complainant.name}</p>
                                    {complaint.complainant.email && (
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Mail className="h-4 w-4" />
                                            <span>{complaint.complainant.email}</span>
                                        </div>
                                    )}
                                    {complaint.complainant.phone && (
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Phone className="h-4 w-4" />
                                            <span>{complaint.complainant.phone}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Assigned Staff */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">ผู้รับผิดชอบ</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {complaint.assignedTo ? (
                                <div className="space-y-1">
                                    <p className="font-medium">{complaint.assignedTo.name}</p>
                                    <p className="text-sm text-muted-foreground">{complaint.assignedTo.email}</p>
                                </div>
                            ) : (
                                <p className="text-muted-foreground">ยังไม่ได้มอบหมาย</p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Dates Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">ข้อมูลวันที่</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3 text-sm">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">วันที่ยื่นเรื่อง</span>
                                <span>
                                    {formatDate(complaint.createdAt)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">ครบกำหนด SLA</span>
                                <span
                                    className={
                                        new Date(complaint.dueDate) < new Date() &&
                                            !["resolved", "closed"].includes(complaint.status)
                                            ? "text-destructive font-medium"
                                            : ""
                                    }
                                >
                                    {formatDate(complaint.dueDate)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">อัปเดตล่าสุด</span>
                                <span>
                                    {formatDate(complaint.updatedAt)}
                                </span>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
